// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { color } from "@bg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
    showQuoteLink?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: InjectableCss;
    wishListInfoGridItem?: InjectableCss;
    wishListInfoGridContainer?: InjectableCss;
    basicInfoGridItem?: InjectableCss;
    basicInfoGridContainer?: InjectableCss;
    detailLinkGridItem?: InjectableCss;
    descriptionGridItem?: InjectableCss;
    linesCount?: InjectableCss;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: InjectableCss;
    lastUpdatedText?: TypographyProps;
    createdUpdatedText?: TypographyProps;
    extendedInfoGridItem?: InjectableCss;
    extendedInfoGridContainer?: InjectableCss;
    productImagesGridItem?: InjectableCss;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: InjectableCss;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: InjectableCss;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ButtonPresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionsOverflowMenu?: InjectableCss;
    actionsHidden?: InjectableCss;
    // Xng-Change
    descriptionWrapper?: InjectableCss;
    toggleHideListTooltip?: TooltipPresentationProps;
    actionHideClickableWrapper?: InjectableCss;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    wishListInfoGridItem: {
        css: css`
            display: flex;
            flex-grow: 1;
            @media (max-width: 600px) {
                flex-wrap: wrap;
            }
        `,
    },
    basicInfoGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            max-width: 500px;
            width: 100%;
            margin-left: 10px;
            @media (max-width: 1350px) {
                max-width: 350px;
            }
            @media (max-width: 600px) {
                max-width: 100%;
                margin-bottom: 15px;
            }
        `,
    },
    sharingStatus: {
        privateIcon: {
            src: "Help",
            size: 14,
            css: css`
                margin-left: 5px;
            `,
        },
        sharedIcon: {
            src: "Users",
            size: 14,
            css: css`
                margin-right: 5px;
            `,
        },
    },
    detailLinkGridItem: {
        css: css`
            font-size: ${fontConfig.fontSize.large}px;
            line-height: ${fontConfig.lineHeight.small};
            @media (max-width: 481px) {
                padding-right: 45px;
            }
        `,
    },
    extendedInfoGridItem: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    linesCount: {
        css: css`
            margin-right: 10px;
        `,
    },
    productImageLink: {
        css: css`
            &&:not(:last-child) {
                margin-right: 20px;
            }
        `,
    },
    productImagesGridItem: {
        css: css`
            width: 250px;
            margin-right: 45px;

            @media (max-width: 1024px) {
                width: 100%;
                margin-bottom: 10px;
            }
            @media (max-width: 600px) {
                margin-right: 0;
            }
        `,
    },
    productImage: {
        css: css`
            max-width: 70px;
            img {
                height: 70px;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    lastUpdatedText: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: "20px",
    },
    createdUpdatedText: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: "20px",
        weight: fontConfig.weight.bold,
    },
    actionsHidden: {
        css: css`
            width: 228px;
            display: flex;
            align-items: center;
        `,
    },
    actionGridItem: {
        css: css`
            max-width: 228px;
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
        `,
    },
    actionAddToCartButton: {
        sizeVariant: "small",
        css: css`
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    actionHideClickable: {
        css: css`
            margin-top: 10px;
            &&&&& {
                color: ${color("text.main")};
                background: transparent;
                border: 0;
                box-shadow: none;
                min-height: auto;
                padding: 0;
                svg {
                    fill: ${getColor("secondary")};
                }
                &&:hover,
                &&:focus {
                    outline: none !important;
                    svg {
                        fill: ${getColor("primary")};
                    }
                }
                * {
                    font-size: ${fontConfig.fontSize.xSmall}px;
                    font-weight: ${fontConfig.weight.regular};
                }
            }
        `,
    },
    actionHideLink: {
        color: "text.main",
        typographyProps: {
            color: "text.main",
        },
        css: css`
            display: block;
            margin: 0;
            &&& {
                padding: 0;
                span {
                    color: ${color("text.main")};
                }
            }
        `,
    },
    actionDeleteButton: {
        css: css`
            &&&&& {
                background: transparent;
                width: auto;
                border: 0;
                box-shadow: none;
                min-height: auto;
                padding: 0;
                svg g {
                    fill: ${getColor("secondary")};
                }
                &&:hover,
                &&:focus {
                    svg g {
                        fill: ${getColor("primary")};
                    }
                }
            }
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionsOverflowMenu: {
        css: css`
            position: absolute;
            top: 15px;
            right: 0;
        `,
    },
    lastUpdatedGridItem: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                padding-right: 40px;
            }
        `,
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            display: flex;
            gap: 4px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
        css: css`
            path {
                fill: ${getColor("primary")};
            }
        `,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
    descriptionWrapper: {
        css: css`
            display: flex;
            gap: 5px;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    toggleHideListTooltip: {
        typographyProps: {
            transform: "none",
            css: css`
                text-align: left;
            `,
        },
    },
    actionHideClickableWrapper: {
        css: css`
            width: 100%;
            order: 2;
            text-align: right;
        `,
    },
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    // check with BE to adjust quantities in case Add-All-List
    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));
    // Xng-Change
    const toggleHideButtonSrc = wishList.isHidden ? "EyeActive" : "EyeInActive";
    const toggleHideButtonSiteMessage = wishList.isHidden
        ? siteMessage("Custom_Toggle_Show_List").toString()
        : siteMessage("Custom_Toggle_Hide_List").toString();

    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector="wishListCard">
            <StyledWrapper {...styles.wishListInfoGridItem} data-test-selector={`${wishList.name}`}>
                <StyledWrapper {...styles.basicInfoGridContainer}>
                    {/* Xng-Change: add a wrapper - descriptionWrapper - */}
                    <StyledWrapper {...styles.descriptionWrapper}>
                        <StyledWrapper {...styles.basicIconGridContainer}>
                            {!wishList.isAutogenerated && (
                                <StyledWrapper {...styles.detailIconGridItem}>
                                    <Clickable
                                        {...styles.actionFavoriteClickable}
                                        onClick={toggleFavorite}
                                        data-test-selector={`myListsFavorite_${wishList.id}`}
                                        disabled={isLoadingAnyList}
                                    >
                                        {wishList.isFavorite && (
                                            <Icon
                                                {...styles.starXIcon}
                                                data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                            />
                                        )}
                                        {!wishList.isFavorite && (
                                            <Icon
                                                {...styles.starIcon}
                                                data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                            />
                                        )}
                                    </Clickable>
                                </StyledWrapper>
                            )}
                            {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                                <StyledWrapper
                                    {...styles.detailIconGridItem}
                                    data-test-selector={`myListsTags_${wishList.id}`}
                                >
                                    <Tooltip
                                        {...styles.tagTooltip}
                                        text={`${translate("Tags Assigned")}: ${wishList.wishListTags
                                            .map(o => o.tag)
                                            .join(", ")}`}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.detailLinkGridItem}>
                            <MyListsDetailsPageTypeLink
                                title={wishList.name}
                                wishListId={wishList.id}
                                testSelector="wishListCardName"
                            />
                        </StyledWrapper>
                        {wishList.description && (
                            <StyledWrapper {...styles.descriptionGridItem}>
                                <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                                    {wishList.description}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.lastUpdatedGridItem}>
                        <StyledWrapper>
                            <Typography {...styles.createdUpdatedText}>
                                {new Date().toDateString() === new Date(wishList.updatedOn).toDateString()
                                    ? translate("Created On:")
                                    : translate("Updated On:")}
                                &nbsp;&nbsp;
                            </Typography>
                            <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                                {updatedOnDisplay}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            {wishList.updatedByDisplayName && !wishList.isAutogenerated && (
                                <Typography {...styles.createdUpdatedText}>&nbsp;{translate("By")}&nbsp;</Typography>
                            )}
                            {wishList.updatedByDisplayName && !wishList.isAutogenerated && (
                                <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardUpdateBy">
                                    {wishList.updatedByDisplayName}
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.extendedInfoGridItem}>
                    <StyledWrapper {...styles.productImagesGridItem} data-test-selector="productImagesGridItem">
                        {wishList.wishListLineCollection &&
                            wishList.wishListLineCollection.slice(0, 3).map(line => (
                                <Link key={line.id.toString()} {...styles.productImageLink} href={line.productUri}>
                                    <LazyImage
                                        {...styles.productImage}
                                        cloudinaryWidth="70"
                                        cloudinary={true}
                                        src={line.smallImagePath}
                                        altText={line.altText}
                                        title={line.altText}
                                    />
                                </Link>
                            ))}
                    </StyledWrapper>
                    {wishList.wishListLinesCount > 0 && (
                        <StyledWrapper {...styles.linesCount}>
                            <Typography size={fontConfig.fontSize.regular} lineHeight="20px">
                                {wishList.wishListLinesCount}&nbsp;{translate("Items")}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.sharingStatusGridItem}>
                        <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Hidden below="md" {...styles.actionsHidden}>
                <StyledWrapper {...styles.actionGridItem}>
                    <Button
                        {...styles.actionAddToCartButton}
                        disabled={!canAddToCart || isUpdatingCart}
                        data-test-selector={`wishListAddToCartButton_${wishList.id}`}
                        onClick={clickAddToCartHandler}
                    >
                        {addToCartLabel}
                    </Button>
                    {/* Xng-Change */}
                    {/* Change toggleHideButtonText to a tooltip with custom icon */}
                    {!wishList.isAutogenerated && (
                        <StyledWrapper {...styles.actionHideClickableWrapper}>
                            <Button {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                                <Tooltip
                                    iconProps={{ src: toggleHideButtonSrc, size: 18 }}
                                    {...styles.toggleHideListTooltip}
                                    text={toggleHideButtonSiteMessage}
                                />
                            </Button>
                        </StyledWrapper>
                    )}
                    {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Button
                                {...styles.actionDeleteButton}
                                onClick={deleteWishList}
                                data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                            >
                                <ButtonIcon src="Delete" size={18} color="seconadry" />
                            </Button>
                        )}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Button
                                {...styles.actionLeaveButton}
                                onClick={leaveWishList}
                                data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                            >
                                {leaveLabel}
                            </Button>
                        )}
                </StyledWrapper>
            </Hidden>
            <Hidden above="sm" {...styles.actionsOverflowMenu}>
                <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                    <Clickable
                        {...styles.actionAddToCartClickable}
                        disabled={!canAddToCart || isUpdatingCart}
                        onClick={clickAddToCartHandler}
                    >
                        {addToCartLabel}
                    </Clickable>
                    {!wishList.isAutogenerated && (
                        <Link
                            {...styles.actionHideLink}
                            onClick={toggleHide}
                            data-test-selector={`myListsHide_${wishList.id}`}
                            disabled={isLoadingAnyList}
                        >
                            {toggleHideButtonText}
                        </Link>
                    )}
                    {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                {translate("Delete List")}
                            </Clickable>
                        )}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                {leaveLabel}
                            </Clickable>
                        )}
                </OverflowMenu>
            </Hidden>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(WishListCard);
